import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API_BASE = 'https://apiclientportal.myidealtd.co.uk';

/* ---------------- IMAGE FIX (IMPORTANT) ---------------- */
const getImageUrl = (item) => {
  const img =
    item?.images?.[0] ||
    item?.image ||
    item?.photo ||
    item?.product?.images?.[0] ||
    item?.product?.image;

  if (!img) return null;

  if (img.startsWith('http')) return img;

  return `https://apiclientportal.myidealtd.co.uk/uploads/${img}`;
};

const OrderSummaryPage = () => {
  const { cart } = useCart();

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const subtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const shipping = subtotal > 100 ? 0 : 5.99;
  const tax = subtotal * 0.2;
  const total = subtotal + shipping + tax;

  /* ---------------- FETCH USER ---------------- */
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/users/profile`,
          { withCredentials: true }
        );

        setUserInfo(data);
      } catch (err) {
        console.error('PROFILE ERROR:', err);
        setError(
          err.response?.data?.message || 'Failed to fetch user details.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  /* ---------------- PLACE ORDER ---------------- */
  const handlePlaceOrder = async () => {
    try {
      const orderData = {
        products: cart.map(item => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          description: item.description,

          // 🔥 FIX: ensure image is ALWAYS stored in DB
          image:
            item?.images?.[0] ||
            item?.image ||
            item?.photo ||
            item?.product?.image ||
            item?.product?.images?.[0] ||
            null
        })),
        subtotal,
        shipping,
        tax,
        total,
        shippingAddress: userInfo?.shippingAddress,
      };

      const { data } = await axios.post(
        `${API_BASE}/api/orders/save-order`,
        orderData,
        { withCredentials: true }
      );

      const checkoutSession = await axios.post(
        `${API_BASE}/api/stripe/create-checkout-session`,
        {
          cart,
          orderId: data._id,
          user: {
            id: userInfo?._id,
            email: userInfo?.email,
          },
          shippingAddress: userInfo?.shippingAddress,
        },
        { withCredentials: true }
      );

      window.location.href = checkoutSession.data.url;
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message || 'Failed to place order.'
      );
    }
  };

  /* ---------------- LOADING ---------------- */
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  /* ---------------- ERROR ---------------- */
  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <div className="card border-danger shadow-sm p-4 text-center">
          <XCircle className="text-danger mb-3" size={40} />
          <p className="text-danger fw-bold mb-0">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="mx-auto" style={{ maxWidth: '750px' }}>
        <div className="card shadow p-4">

          <h2 className="text-center mb-4">🧾 Order Summary</h2>

          {cart.length > 0 ? (
            <>
              {/* USER */}
              {userInfo && (
                <div className="mb-4">
                  <div className="row">
                    <div className="col-md-6">
                      <h5>👤 Customer</h5>
                      <p className="mb-0">{userInfo.name}</p>
                    </div>

                    <div className="col-md-6 text-md-end">
                      <h5>🏠 Shipping</h5>

                      {userInfo.shippingAddress ? (
                        <address className="text-muted mb-0">
                          {userInfo.shippingAddress.address}
                          <br />
                          {userInfo.shippingAddress.city},{' '}
                          {userInfo.shippingAddress.postcode}
                          <br />
                          {userInfo.shippingAddress.country}
                        </address>
                      ) : (
                        <p className="text-muted">
                          No shipping address available
                        </p>
                      )}
                    </div>
                  </div>
                  <hr />
                </div>
              )}

              {/* PRODUCTS */}
              {cart.map((item) => {
                const imageUrl = getImageUrl(item);

                return (
                  <div
                    key={item._id}
                    className="row border-bottom pb-3 mb-3 align-items-center"
                  >
                    <div className="col-3">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={item.name}
                          style={{
                            width: 70,
                            height: 70,
                            objectFit: 'cover',
                            borderRadius: 10,
                            border: '1px solid #eee'
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 70,
                            height: 70,
                            background: '#eee',
                            borderRadius: 10
                          }}
                        />
                      )}
                    </div>

                    <div className="col-5">
                      <p className="mb-0 fw-semibold">{item.name}</p>
                      <small className="text-muted">
                        Qty: {item.quantity}
                      </small>
                    </div>

                    <div className="col-4 text-end fw-bold">
                      £{item.price.toFixed(2)}
                    </div>
                  </div>
                );
              })}

              {/* TOTAL */}
              <div className="border-top pt-3">
                <div className="d-flex justify-content-between">
                  <span>Subtotal</span>
                  <span>£{subtotal.toFixed(2)}</span>
                </div>

                <div className="d-flex justify-content-between">
                  <span>Shipping</span>
                  <span>£{shipping.toFixed(2)}</span>
                </div>

                <div className="d-flex justify-content-between">
                  <span>Tax</span>
                  <span>£{tax.toFixed(2)}</span>
                </div>

                <div className="d-flex justify-content-between fw-bold fs-5 mt-2 border-top pt-2">
                  <span>Total</span>
                  <span>£{total.toFixed(2)}</span>
                </div>
              </div>

              <button
                className="btn btn-primary w-100 mt-4"
                onClick={handlePlaceOrder}
              >
                <CheckCircle size={18} className="me-2" />
                Place Order
              </button>
            </>
          ) : (
            <p className="text-center text-muted">
              Your cart is empty
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderSummaryPage;